/**
 * ProfileViewsModal.tsx
 *
 * File-level:
 * - Accessible modal that displays UNIQUE profile viewers (aggregated by userId) and handles endpoint fallbacks.
 * - Visual update: modal styled to match ShortlistModal:
 *    - Light pink background (bg-rose-50)
 *    - Max width constrained to max-w-3xl and max height tuned to show 2 rows of a 3x2 grid
 *    - Grid display uses 3 columns x 2 rows (6 items per page) with larger vertical gaps
 *    - Each profile card uses a thicker colored border for clear separation
 *
 * Notes:
 * - Hooks are declared unconditionally at the top of the component to avoid Rules of Hooks violations.
 * - Uses fetchJSON and apiBase from '@/utils/api' so runtime API base overrides work.
 * - Persists lightweight avatar/profession caches to sessionStorage (fallback localStorage) with TTL.
 * - Shows toasts for refresh success and when batch profession preload loads N professions.
 */

import React, { useEffect, useMemo, useRef, useState, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { X, Grid, List, User, Eye, AlertCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { fetchJSON, apiBase } from '@/utils/api';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

/**
 * RawViewerEvent
 * @interface
 * Represents a single view event returned by backend (may contain many rows per viewer)
 */
interface RawViewerEvent {
  userId: string;
  firstName?: string;
  lastName?: string;
  viewedAt?: string | null;
  profession?: string | null;
  avatarUrl?: string | null;
}

/**
 * UniqueViewer
 * @interface
 * Aggregated unique viewer with hits and lastViewedAt
 */
interface UniqueViewer {
  userId: string;
  firstName?: string;
  lastName?: string;
  profession?: string | null;
  avatarUrl?: string | null;
  hits: number;
  lastViewedAt?: string | null;
}

/**
 * ProfileViewsModalProps
 * @interface
 */
export interface ProfileViewsModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Optional profileId (owner) to fetch viewers for */
  profileId?: string | null;
}

/**
 * normalizeViewersResponse
 * @description Normalize multiple backend shapes to RawViewerEvent[]
 * @param json any
 */
function normalizeViewersResponse(json: any): RawViewerEvent[] {
  if (!json) return [];
  const raw = json.viewers ?? json.data?.viewers ?? json.data ?? json;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((r: any) => ({
        userId: String(r.userId ?? r.profileId ?? r.id ?? r.viewerId ?? r.viewerId ?? ''),
        firstName: r.firstName ?? r.first_name ?? '',
        lastName: r.lastName ?? r.last_name ?? '',
        viewedAt: r.viewedAt ?? r.viewed_at ?? r.createdAt ?? null,
        profession: r.profession ?? null,
        avatarUrl: r.avatarUrl ?? r.photoUrl ?? r.url ?? r.filePath ?? null,
      }))
      .filter((x: RawViewerEvent) => !!x.userId);
  }
  return [];
}

/**
 * aggregateUniqueViewers
 * @description Aggregate RawViewerEvent[] into UniqueViewer[] keyed by userId
 * - counts hits
 * - uses latest viewedAt as lastViewedAt
 */
function aggregateUniqueViewers(events: RawViewerEvent[]): UniqueViewer[] {
  const map = new Map<string, UniqueViewer>();
  for (const e of events) {
    const id = String(e.userId);
    if (!map.has(id)) {
      map.set(id, {
        userId: id,
        firstName: e.firstName ?? '',
        lastName: e.lastName ?? '',
        profession: e.profession ?? null,
        avatarUrl: e.avatarUrl ?? null,
        hits: 1,
        lastViewedAt: e.viewedAt ?? null,
      });
    } else {
      const cur = map.get(id)!;
      cur.hits = (cur.hits || 0) + 1;
      // prefer most recent viewedAt
      try {
        const curTs = cur.lastViewedAt ? new Date(cur.lastViewedAt).getTime() : 0;
        const newTs = e.viewedAt ? new Date(e.viewedAt).getTime() : 0;
        if (newTs > curTs) {
          cur.lastViewedAt = e.viewedAt ?? cur.lastViewedAt;
        }
        // fill missing name/profession/avatar if present on event
        if (!cur.firstName && e.firstName) cur.firstName = e.firstName;
        if (!cur.lastName && e.lastName) cur.lastName = e.lastName;
        if (!cur.profession && e.profession) cur.profession = e.profession;
        if (!cur.avatarUrl && e.avatarUrl) cur.avatarUrl = e.avatarUrl;
      } catch {
        // ignore date parse errors
      }
    }
  }
  return Array.from(map.values());
}

/**
 * resolveAbsoluteMediaUrl
 * @description Ensure relative paths use runtime apiBase()
 * @param pathOrUrl string | null | undefined
 */
function resolveAbsoluteMediaUrl(pathOrUrl: string | null | undefined) {
  if (!pathOrUrl) return '';
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  try {
    const base = String(apiBase?.() ?? '').replace(/\/$/, '');
    if (!base) return pathOrUrl;
    const p = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
    return `${base}${p}`;
  } catch {
    return pathOrUrl;
  }
}

/**
 * buildAvatarFallback
 * @description Small deterministic placeholder when no avatar is available
 * @param item UniqueViewer | undefined
 */
function buildAvatarFallback(item?: UniqueViewer) {
  return `https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/cf5456ab-779a-47a2-a049-7af687bd26c1.jpg`;
}

/**
 * shouldMarkEndpointFailed
 * @description Decide whether a caught error should mark an endpoint failed for the session.
 * Only mark for HTTP 404 Not Found or explicit not-found messages.
 */
function shouldMarkEndpointFailed(err: any): boolean {
  try {
    if (!err) return false;
    if (err && typeof err === 'object') {
      const status = (err.status ?? err.statusCode ?? err.code) as number | undefined;
      if (status === 404) return true;
      const msg = String(err?.message ?? err?.msg ?? '').toLowerCase();
      if (msg.includes('404') || msg.includes('not found')) return true;
    }
    const s = String(err ?? '').toLowerCase();
    if (s.includes('404') || s.includes('not found')) return true;
  } catch {
    // ignore parsing errors
  }
  return false;
}

/**
 * ProfileViewsModal
 *
 * - Responsible for showing UNIQUE viewers (aggregated) and their hit counts.
 * - Shows small ProfileUserID under the name and "Total No.of times Viewed" for each profile.
 * - Tries to batch-preload professions in batch and shows a neutral toast when professions are loaded.
 * - Presentation updated to mirror ShortlistModal (3x2 grid cards, light-pink background, thick borders).
 */
export default function ProfileViewsModal({
  isOpen,
  onClose,
  profileId = null,
}: ProfileViewsModalProps): JSX.Element | null {
  // Refs & navigation
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const prevFocusedRef = useRef<Element | null>(null);
  const navigate = useNavigate();

  // UI & data states (all hooks declared unconditionally)
  const [uniqueViewers, setUniqueViewers] = useState<UniqueViewer[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [layout, setLayout] = useState<'grid' | 'list'>('grid');
  const [compact, setCompact] = useState<boolean>(false);

  // pagination (server may paginate; we enforce client page display to 6 items per page)
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(6); // 6 items per page -> 3 x 2
  const [totalUnique, setTotalUnique] = useState<number | null>(null);

  // header stats
  const [uniqueViewersCount, setUniqueViewersCount] = useState<number | null>(null);
  const [lastViewedAt, setLastViewedAt] = useState<string | null>(null);

  // caches & inflight trackers (in-memory)
  const avatarCacheRef = useRef<Map<string, string | null>>(new Map());
  const profCacheRef = useRef<Map<string, string | null>>(new Map());
  const inflightAvatar = useRef<Record<string, boolean>>({});
  const inflightProf = useRef<Record<string, boolean>>({});

  // persistent cache keys and TTL (6 hours)
  const AVATAR_KEY = 'pv_modal_avatar_v1';
  const PROF_KEY = 'pv_modal_prof_v1';
  const CACHE_TTL = 1000 * 60 * 60 * 6; // 6 hours

  /**
   * failedEndpointsRef
   * @description In-memory set of endpoint keys that recently failed (to avoid repeated 404s)
   */
  const failedEndpointsRef = useRef<Set<string>>(new Set());

  function markEndpointFailed(key: string) {
    try {
      failedEndpointsRef.current.add(key);
      // eslint-disable-next-line no-console
      console.warn(`[ProfileViewsModal] marking endpoint as failed for session: ${key}`);
    } catch {
      // ignore
    }
  }

  function clearEndpointFailure(key: string) {
    try {
      failedEndpointsRef.current.delete(key);
    } catch {
      // ignore
    }
  }

  function isEndpointFailed(key: string) {
    return failedEndpointsRef.current.has(key);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === 'Escape') onClose();
  }

  useEffect(() => {
    if (!isOpen) return;
    prevFocusedRef.current = document.activeElement;
    setTimeout(() => dialogRef.current?.focus(), 0);
    window.addEventListener('keydown', handleKeyDown as any);
    const orig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    loadCaches();

    return () => {
      window.removeEventListener('keydown', handleKeyDown as any);
      document.body.style.overflow = orig;
      try {
        (prevFocusedRef.current as HTMLElement | null)?.focus?.();
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  /**
   * loadCaches
   * Load caches from sessionStorage or localStorage with TTL validation.
   */
  function loadCaches() {
    try {
      const raw = sessionStorage.getItem(AVATAR_KEY) ?? localStorage.getItem(AVATAR_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, { value: string | null; expires: number }>;
        const now = Date.now();
        Object.entries(parsed).forEach(([k, v]) => {
          if (v && v.expires > now) avatarCacheRef.current.set(k, v.value);
        });
      }
    } catch {
      // ignore
    }
    try {
      const raw = sessionStorage.getItem(PROF_KEY) ?? localStorage.getItem(PROF_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, { value: string | null; expires: number }>;
        const now = Date.now();
        Object.entries(parsed).forEach(([k, v]) => {
          if (v && v.expires > now) profCacheRef.current.set(k, v.value);
        });
      }
    } catch {
      // ignore
    }
  }

  function persistCache(key: string, map: Map<string, string | null>) {
    try {
      const obj: Record<string, { value: string | null; expires: number }> = {};
      const expires = Date.now() + CACHE_TTL;
      map.forEach((v, k) => {
        obj[k] = { value: v, expires };
      });
      const str = JSON.stringify(obj);
      try {
        sessionStorage.setItem(key, str);
      } catch {
        localStorage.setItem(key, str);
      }
    } catch {
      // ignore
    }
  }

  function setAvatarCache(uid: string, url: string | null) {
    avatarCacheRef.current.set(uid, url);
    persistCache(AVATAR_KEY, avatarCacheRef.current);
  }

  function setProfCache(uid: string, prof: string | null) {
    profCacheRef.current.set(uid, prof);
    persistCache(PROF_KEY, profCacheRef.current);
  }

  /**
   * fetchHeaderStats
   * @description Fetch summary counts and optionally show a success toast when showToast=true
   */
  async function fetchHeaderStats(showToast = false) {
    if (!profileId) {
      setUniqueViewersCount(null);
      setLastViewedAt(null);
      return;
    }
    try {
      const ep = `/dashboard/profile-views/count/${encodeURIComponent(profileId)}`;
      const resp = await fetchJSON(ep);
      const data = resp?.data ?? resp;
      const uniqueVal = typeof data?.uniqueViewers === 'number' ? data.uniqueViewers : (data?.uniqueViewers ? Number(data.uniqueViewers) : null);
      const lastVal = data?.lastViewedAt ?? null;
      setUniqueViewersCount(uniqueVal);
      setLastViewedAt(lastVal);

      if (showToast) {
        const uText = typeof uniqueVal === 'number' ? `${uniqueVal} unique` : '— unique';
        const lastText = lastVal ? formatShortDate(lastVal) : 'no recent';
        toast.success(`${uText} • Last: ${lastText}`);
      }
    } catch (err) {
      if (shouldMarkEndpointFailed(err)) markEndpointFailed('dashboard.profileViews.count');
      // eslint-disable-next-line no-console
      console.warn('[ProfileViewsModal] fetchHeaderStats failed:', err?.message ?? err);
    }
  }

  /**
   * fetchBatchUsers
   * @description Try POST /users/batch with { ids } then GET /users/batch?ids=..
   * Returns map userId -> profession (string|null)
   */
  async function fetchBatchUsers(ids: string[]): Promise<Record<string, string | null>> {
    const out: Record<string, string | null> = {};
    if (!ids || ids.length === 0) return out;
    const uniq = Array.from(new Set(ids)).filter(Boolean);
    if (uniq.length === 0) return out;

    const batchKeyPost = 'users.batch.post';
    const batchKeyGet = 'users.batch.get';

    // Try POST
    try {
      if (!isEndpointFailed(batchKeyPost)) {
        const resp = await fetchJSON('/users/batch', { method: 'POST', body: JSON.stringify({ ids: uniq }) });
        const data = resp?.data ?? resp;
        const users = data?.users ?? data;
        if (Array.isArray(users)) {
          users.forEach((u: any) => {
            const id = String(u.userId ?? u.id ?? u.user_id ?? '');
            const prof = String(u.profession ?? u.job ?? u.occupation ?? '') || null;
            if (id) out[id] = prof;
          });
          return out;
        }
      }
    } catch (err: any) {
      if (shouldMarkEndpointFailed(err)) markEndpointFailed(batchKeyPost);
    }

    // Try GET
    try {
      if (!isEndpointFailed(batchKeyGet)) {
        const q = uniq.map(encodeURIComponent).join(',');
        const resp = await fetchJSON(`/users/batch?ids=${q}`);
        const data = resp?.data ?? resp;
        const users = data?.users ?? data;
        if (Array.isArray(users)) {
          users.forEach((u: any) => {
            const id = String(u.userId ?? u.id ?? u.user_id ?? '');
            const prof = String(u.profession ?? u.job ?? u.occupation ?? '') || null;
            if (id) out[id] = prof;
          });
          return out;
        }
      }
    } catch (err: any) {
      if (shouldMarkEndpointFailed(err)) markEndpointFailed(batchKeyGet);
    }

    return out;
  }

  /**
   * preloadProfessionsBatch
   * @description For first page: try to preload professions in batch and update caches + UI.
   * Shows a neutral toast indicating how many professions were loaded via batch.
   */
  async function preloadProfessionsBatch(rows: UniqueViewer[]) {
    try {
      if (!rows || rows.length === 0) return;
      if (page !== 1) return; // only for the first page

      const missingIds = rows.filter(r => !r.profession && !profCacheRef.current.has(r.userId)).map(r => r.userId);
      if (missingIds.length === 0) return;

      const batchResult = await fetchBatchUsers(missingIds);
      const foundIds = Object.keys(batchResult);
      let loadedCount = 0;
      foundIds.forEach((id) => {
        const prof = batchResult[id] ?? null;
        setProfCache(id, prof);
        if (prof !== null && String(prof).trim().length > 0) loadedCount++;
      });

      // Update current uniqueViewers UI with any found professions
      if (foundIds.length > 0) {
        setUniqueViewers(prev => prev.map(v => {
          if (batchResult[v.userId] !== undefined) {
            return { ...v, profession: batchResult[v.userId] ?? v.profession ?? null };
          }
          return v;
        }));
      }

      if (loadedCount > 0) {
        // Neutral toast indicating how many professions were loaded via batch
        toast(`Loaded professions for ${loadedCount} profiles`);
      }

      // For any still missing, fall back to per-user fetch concurrently
      const stillMissing = missingIds.filter(id => !profCacheRef.current.has(id));
      await Promise.all(stillMissing.map(id => fetchProfessionForUser(id).catch(() => {})));
    } catch (err) {
      // non-fatal
      // eslint-disable-next-line no-console
      console.warn('[ProfileViewsModal] preloadProfessionsBatch failed', err);
    }
  }

  /**
   * fetchAllRawViewEvents
   * @description When server-side aggregated endpoint is unavailable, page through the raw list endpoint
   *              to collect all events. Uses list endpoint with page-offset style calls (limit + offset).
   * @param profileId string
   * @returns RawViewerEvent[] - combined events from all pages
   */
  async function fetchAllRawViewEvents(profileId: string): Promise<RawViewerEvent[]> {
    const combined: RawViewerEvent[] = [];
    const perPage = 200; // match backend sensible upper limit
    let offset = 0;
    try {
      while (true) {
        const ep = `/dashboard/profile-views/list/${encodeURIComponent(profileId)}?limit=${perPage}&offset=${offset}`;
        const resp = await fetchJSON(ep);
        // normalize rows
        const rows = resp?.data?.viewers ?? resp?.viewers ?? resp;
        let normalized: RawViewerEvent[] = [];
        if (Array.isArray(rows)) {
          normalized = rows.map((r: any) => ({
            userId: String(r.viewerId ?? r.userId ?? r.id ?? r.profileId ?? ''),
            firstName: r.firstName ?? r.first_name ?? '',
            lastName: r.lastName ?? r.last_name ?? '',
            viewedAt: r.viewDate ?? r.viewDate ?? r.viewedAt ?? r.viewed_at ?? r.createdAt ?? null,
            profession: r.profession ?? null,
            avatarUrl: r.avatarUrl ?? r.photoUrl ?? null,
          })).filter(x => !!x.userId);
        }
        combined.push(...normalized);
        // If rows length less than perPage => last page
        const fetchedCount = Array.isArray(rows) ? rows.length : 0;
        if (fetchedCount < perPage) break;
        offset += perPage;
      }
    } catch (err: any) {
      // If this fails, bubble up so caller may try alternate fallback
      throw err;
    }
    return combined;
  }

  /**
   * fetchViewersPage
   * @description Fetch aggregated unique viewers from the server (preferred).
   * Falls back to older endpoints. When falling back to raw endpoints, gather ALL events
   * (iterating pages) then aggregate client-side to ensure accurate counts and viewers.
   */
  async function fetchViewersPage(pageParam = page, limitParam = limit) {
    if (!profileId) {
      setUniqueViewers([]);
      setTotalUnique(null);
      return;
    }

    setLoading(true);
    setError(null);

    // Try aggregated endpoint first (returns unique viewers with hits)
    try {
      if (!isEndpointFailed('dashboard.profileViews.aggregated')) {
        const epAgg = `/dashboard/profile-views/aggregated/${encodeURIComponent(profileId)}?page=${pageParam}&limit=${limitParam}`;
        const resp = await fetchJSON(epAgg);

        if (resp && resp.success && resp.data && Array.isArray(resp.data.viewers)) {
          const viewers = resp.data.viewers.map((r: any) => ({
            userId: String(r.viewerId ?? r.userId ?? ''),
            firstName: r.firstName ?? '',
            lastName: r.lastName ?? '',
            profession: r.profession ?? null,
            avatarUrl: r.avatarUrl ?? null,
            hits: Number(r.hits ?? 0),
            lastViewedAt: r.lastViewedAt ?? null,
          })) as UniqueViewer[];

          setUniqueViewers(viewers);
          setTotalUnique(Number(resp.data.totalUnique ?? viewers.length));
          // update header stats (no toast)
          fetchHeaderStats().catch(() => {});
          // preload professions for first page
          preloadProfessionsBatch(viewers).catch(() => {});
          // background fetch avatars & professions
          viewers.forEach(v => {
            if ((!v.avatarUrl || (typeof v.avatarUrl === 'string' && v.avatarUrl.trim() === '')) && !avatarCacheRef.current.has(v.userId) && !inflightAvatar.current[v.userId]) {
              fetchAvatarForUser(v.userId).catch(() => {});
            } else if (v.avatarUrl && !avatarCacheRef.current.has(v.userId)) {
              setAvatarCache(v.userId, resolveAbsoluteMediaUrl(v.avatarUrl));
            }
            if (v.profession && !profCacheRef.current.has(v.userId)) {
              setProfCache(v.userId, String(v.profession));
            }
          });

          setLoading(false);
          return;
        } else if (resp && resp.success && resp.data && !Array.isArray(resp.data.viewers)) {
          // Aggregated responded but shape unexpected; mark potential failure
          markEndpointFailed('dashboard.profileViews.aggregated');
        }
      }
    } catch (err: any) {
      if (shouldMarkEndpointFailed(err)) markEndpointFailed('dashboard.profileViews.aggregated');
      // fall through to fallback logic
    }

    // Fallback: gather ALL raw view events (page through) and aggregate client-side
    try {
      const endpointKey = 'dashboard.profileViews.listall';
      if (isEndpointFailed(endpointKey)) {
        // eslint-disable-next-line no-console
        console.warn(`[ProfileViewsModal] skipping fallback because endpoint marked failed: ${endpointKey}`);
        setUniqueViewers([]);
        setTotalUnique(null);
        setLoading(false);
        return;
      }

      try {
        const events = await fetchAllRawViewEvents(profileId);
        // aggregate to unique viewers
        const unique = aggregateUniqueViewers(events);
        setUniqueViewers(unique);
        setTotalUnique(unique.length);

        // update header stats (no toast)
        fetchHeaderStats().catch(() => {});
        // Kick off batch profession preload for first page
        preloadProfessionsBatch(unique).catch(() => { /* ignore */ });

        // background resolution of avatars & professions for any remaining items
        unique.forEach(v => {
          if ((!v.avatarUrl || (typeof v.avatarUrl === 'string' && v.avatarUrl.trim() === '')) && !avatarCacheRef.current.has(v.userId) && !inflightAvatar.current[v.userId]) {
            fetchAvatarForUser(v.userId).catch(() => { /* ignore */ });
          } else if (v.avatarUrl && !avatarCacheRef.current.has(v.userId)) {
            setAvatarCache(v.userId, resolveAbsoluteMediaUrl(v.avatarUrl));
          }
          // If profession present on row, cache it
          if (v.profession && !profCacheRef.current.has(v.userId)) {
            setProfCache(v.userId, String(v.profession));
          }
        });
      } catch (innerErr: any) {
        // If the list paging approach fails, try the /recent or /viewers once as final fallback
        if (shouldMarkEndpointFailed(innerErr)) markEndpointFailed(endpointKey);
        try {
          const alt = await fetchJSON(`/dashboard/profile-views/recent/${encodeURIComponent(profileId)}?page=${pageParam}&limit=${limitParam}`);
          const events = normalizeViewersResponse(alt);
          const unique = aggregateUniqueViewers(events);
          setUniqueViewers(unique);
          setTotalUnique(unique.length);
          fetchHeaderStats().catch(() => { /* ignore */ });
          preloadProfessionsBatch(unique).catch(() => {});
        } catch (fallbackErr: any) {
          if (shouldMarkEndpointFailed(fallbackErr)) markEndpointFailed('dashboard.profileViews.recent');
          setError(String(fallbackErr?.message ?? 'Failed to load viewers'));
          setUniqueViewers([]);
          setTotalUnique(null);
        }
      }
    } catch (err: any) {
      setError(String(err?.message ?? 'Failed to load viewers'));
      setUniqueViewers([]);
      setTotalUnique(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isOpen) return;
    fetchViewersPage();
    fetchHeaderStats().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, profileId, page, limit]);

  /**
   * fetchAvatarForUser
   * - Try several endpoints and cache result
   */
  async function fetchAvatarForUser(uid: string) {
    if (!uid) return null;
    if (avatarCacheRef.current.has(uid)) return avatarCacheRef.current.get(uid) ?? null;

    const endpointKey1 = 'dashboard.userPhotos';
    const endpointKey2 = 'userFiles.photos';
    const endpointKey3 = 'users.get';

    inflightAvatar.current[uid] = true;
    try {
      if (!isEndpointFailed(endpointKey1)) {
        try {
          const j = await fetchJSON(`/dashboard/user-photos/${encodeURIComponent(uid)}`);
          if (j && Array.isArray(j.photos) && j.photos.length > 0) {
            const p = j.photos[0];
            const cand = p.src || p.photoUrl || p.url || p.filePath || p.fileName;
            if (cand) {
              const resolved = resolveAbsoluteMediaUrl(cand);
              setAvatarCache(uid, resolved);
              return resolved;
            }
          }
        } catch (err: any) {
          if (shouldMarkEndpointFailed(err)) markEndpointFailed(endpointKey1);
        }
      }

      if (!isEndpointFailed(endpointKey2)) {
        try {
          const j = await fetchJSON(`/user-files/${encodeURIComponent(uid)}/Photos`);
          if (j && Array.isArray(j.files) && j.files.length > 0) {
            const f = j.files[0];
            const fileName = f.fileName || f.name;
            if (fileName) {
              const host = String(apiBase?.() ?? '').replace(/\/$/, '').replace(/\/api$/, '');
              const resolved = `${host}/Media/${uid}/Photos/${fileName}`;
              setAvatarCache(uid, resolved);
              return resolved;
            }
          }
        } catch (err: any) {
          if (shouldMarkEndpointFailed(err)) markEndpointFailed(endpointKey2);
        }
      }

      if (!isEndpointFailed(endpointKey3)) {
        try {
          const ures = await fetchJSON(`/users/${encodeURIComponent(uid)}`);
          const u = ures?.user ?? ures?.data ?? ures;
          const candidates = [u?.avatarUrl, u?.photoUrl, u?.profilePhoto, u?.photo, u?.image];
          for (const cand of candidates) {
            if (cand) {
              const resolved = resolveAbsoluteMediaUrl(cand);
              setAvatarCache(uid, resolved);
              return resolved;
            }
          }
        } catch (err: any) {
          if (shouldMarkEndpointFailed(err)) markEndpointFailed(endpointKey3);
        }
      }

      setAvatarCache(uid, null);
      return null;
    } finally {
      delete inflightAvatar.current[uid];
    }
  }

  /**
   * fetchProfessionForUser
   * - Prefer users table then profiles fallback
   * - Cache result and update UI entry
   */
  async function fetchProfessionForUser(uid: string) {
    console.log('[ProfileViewsModal] ========== fetchProfessionForUser START ==========');
    console.log('[ProfileViewsModal] Called for uid:', uid);
    if (!uid) return null;
    if (profCacheRef.current.has(uid)) return profCacheRef.current.get(uid) ?? null;
    
    console.log('[ProfileViewsModal] Not in cache, fetching from API...');

    const endpointKeyUsers = 'users.get';
    const endpointKeyProfiles = 'profiles.user';

    inflightProf.current[uid] = true;
    try {
      if (!isEndpointFailed(endpointKeyUsers)) {
        try {
          console.log('[ProfileViewsModal] Fetching profession from /users/' + uid);
          const res = await fetchJSON(`/users/${encodeURIComponent(uid)}`);
          console.log('[ProfileViewsModal] /users response:', res);
          const u = res?.user ?? res?.data ?? res;
          console.log('[ProfileViewsModal] Extracted user:', u);
          const profVal = u && (u.profession ?? u.job ?? u.occupation);
          if (profVal && String(profVal).trim() !== '') {
            const prof = String(profVal);
            console.log('[ProfileViewsModal] Found profession:', prof);
            setProfCache(uid, prof);
            setUniqueViewers(prev => prev.map(v => v.userId === uid ? { ...v, profession: prof } : v));
            return prof;
          }
          console.log('[ProfileViewsModal] No profession in /users response');
        } catch (err: any) {
          console.log('[ProfileViewsModal] /users call failed:', err);
          if (shouldMarkEndpointFailed(err)) markEndpointFailed(endpointKeyUsers);
        }
      }

      if (!isEndpointFailed(endpointKeyProfiles)) {
        try {
          console.log('[ProfileViewsModal] Trying /profiles fallback for:', uid);
          const pres = await fetchJSON(`/profiles/user/${encodeURIComponent(uid)}?minimal=1`);
          console.log('[ProfileViewsModal] /profiles response:', pres);
          const p = pres?.profile ?? pres?.data ?? pres;
          console.log('[ProfileViewsModal] Extracted profile:', p);
          const profVal = p?.basicDetails?.profession ?? p?.basic?.profession ?? p?.profession ?? p?.personalDetails?.occupation ?? p?.personal?.occupation;
          if (profVal && String(profVal).trim() !== '') {
            const prof = String(profVal);
            console.log('[ProfileViewsModal] Found profession in profiles:', prof);
            setProfCache(uid, prof);
            setUniqueViewers(prev => prev.map(v => v.userId === uid ? { ...v, profession: prof } : v));
            return prof;
          }
          console.log('[ProfileViewsModal] No profession in /profiles response');
        } catch (err: any) {
          console.log('[ProfileViewsModal] /profiles call failed:', err);
          if (shouldMarkEndpointFailed(err)) markEndpointFailed(endpointKeyProfiles);
        }
      }

      console.log('[ProfileViewsModal] No profession found for:', uid);
      setProfCache(uid, null);
      return null;
    } finally {
      delete inflightProf.current[uid];
    }
  }

  function getAvatar(uid: string) {
    const v = avatarCacheRef.current.get(uid);
    if (typeof v === 'string') return v;
    return null;
  }

  function getProfession(uid: string) {
    const v = profCacheRef.current.get(uid);
    if (typeof v === 'string') return v;
    return null;
  }

  function formatShortDate(dt: string | null | undefined) {
    if (!dt) return '—';
    try {
      const s = String(dt).trim();
      // If server returned a SQL-style timestamp (e.g. "2026-01-04 21:50:10"),
      // display it verbatim so we don't apply local timezone conversions.
      const sqlLike = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
      if (sqlLike.test(s)) return s;
      // If ISO with 'T' and trailing Z, convert to a space-separated form but keep original moment
      // (drop the Z so it matches DB-style presentation)
      if (/^\d{4}-\d{2}-\d{2}T.*Z$/.test(s)) return s.replace('T', ' ').replace(/Z$/, '');
      // If ISO without explicit timezone, prefer the original string (avoid implicit local conversion)
      if (/^\d{4}-\d{2}-\d{2}T/.test(s)) return s.replace('T', ' ');
      // Fallback: try parsing and localizing if it's a parsable date
      const d = new Date(s);
      return isNaN(d.getTime()) ? s : d.toLocaleString();
    } catch {
      return String(dt);
    }
  }

  /**
   * Avatar component (small, local)
   * Renders image (if available) and initials fallback.
   */
  function Avatar({ uid, size = 56 }: { uid: string; size?: number }) {
    const src = getAvatar(uid);
    const viewer = uniqueViewers.find(v => v.userId === uid);
    const initials = `${(viewer?.firstName || '').charAt(0) || ''}${(viewer?.lastName || '').charAt(0) || ''}`.toUpperCase();
    const fallback = buildAvatarFallback(viewer as any);

    const cachedProf = profCacheRef.current.get(uid);
    console.log('[ProfileViewsModal] Avatar rendered for uid:', uid);
    console.log('[ProfileViewsModal] Cached profession value:', cachedProf);

    // If cached profession is null or empty, remove it and fetch again
    if (profCacheRef.current.has(uid) && (!cachedProf || cachedProf.trim() === '')) {
      console.log('[ProfileViewsModal] Removing null/empty profession from cache for:', uid);
      profCacheRef.current.delete(uid);
    }

    // kick off background fetches as needed
    if (!avatarCacheRef.current.has(uid) && !inflightAvatar.current[uid]) {
      console.log('[ProfileViewsModal] Triggering fetchAvatarForUser for:', uid);
      fetchAvatarForUser(uid).catch(() => { /* ignore */ });
    }
    if (!profCacheRef.current.has(uid) && !inflightProf.current[uid]) {
      console.log('[ProfileViewsModal] Triggering fetchProfessionForUser for:', uid);
      fetchProfessionForUser(uid).catch(() => { /* ignore */ });
    }

    return (
      <div
        className="relative rounded-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center"
        style={{ width: size, height: size }}
        aria-hidden
      >
        <img
          src={src ?? fallback}
          alt={viewer ? `${viewer.firstName || ''} ${viewer.lastName || ''}` : 'viewer'}
          className="w-full h-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          loading="lazy"
          decoding="async"
        />
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="text-slate-700 font-semibold">{initials || '—'}</span>
        </div>
      </div>
    );
  }

  const skeletonCount = 6;

  const totalPages = useMemo(() => {
    if (!totalUnique) return 1;
    return Math.max(1, Math.ceil((totalUnique || 0) / limit));
  }, [totalUnique, limit]);

  if (!isOpen) return null;

  function clearViewerEndpointFailures() {
    clearEndpointFailure('dashboard.profileViews.viewers');
    clearEndpointFailure('dashboard.profileViews.recent');
    clearEndpointFailure('dashboard.profileViews.count');
    clearEndpointFailure('dashboard.profileViews.aggregated');
    clearEndpointFailure('dashboard.profileViews.listall');
  }

  const viewersEndpointsMissing =
    isEndpointFailed('dashboard.profileViews.viewers') && isEndpointFailed('dashboard.profileViews.recent') && isEndpointFailed('dashboard.profileViews.aggregated');

  // Ensure displayed items follow 3x2 pagination even when server returned a larger array
  const displayedItems = uniqueViewers.length > 0
    ? uniqueViewers.slice((page - 1) * limit, (page - 1) * limit + limit)
    : [];

  return createPortal(
    <div className="fixed inset-0 z-[100000] flex items-start justify-center p-6 pointer-events-none" aria-hidden={!isOpen}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        className="relative w-full max-w-3xl mt-12 rounded-lg shadow-2xl ring-1 ring-slate-200 overflow-hidden pointer-events-auto"
      >
        {/* Modal container with light pink background similar to ShortlistModal */}
        <div className="bg-rose-50 rounded-lg overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Profile Views</h3>
                <p className="text-sm opacity-90">Unique profiles who viewed your profile</p>
              </div>
              <div className="ml-4 text-sm flex items-center gap-2">
                <div className="bg-white/20 text-white text-sm px-3 py-1 rounded-full font-medium">Unique: {uniqueViewersCount ?? (totalUnique ?? '—')}</div>
                <div className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">Last: {formatShortDate(lastViewedAt)}</div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button size="sm" className="bg-transparent text-white border-white/30" onClick={() => { setPage(1); fetchViewersPage(1, limit); fetchHeaderStats(true); }}>
                Refresh
              </Button>

              <Button size="sm" className="bg-transparent text-white border-white/30" onClick={onClose} aria-label="Close profile views">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Content - constrained to match ShortlistModal sizing */}
          <div className="p-6" style={{ maxHeight: '560px', overflow: 'auto' }}>
            {viewersEndpointsMissing ? (
              <div className="py-12 text-center">
                <div className="mx-auto w-28 h-28 rounded-full bg-yellow-50 flex items-center justify-center mb-4">
                  <AlertCircle className="w-10 h-10 text-amber-600" />
                </div>
                <h4 className="text-lg font-semibold text-slate-800 mb-2">Profile views currently unavailable</h4>
                <p className="text-sm text-slate-500 mb-4">The server-side profile-views endpoints are not available in this environment.</p>
                <div className="flex items-center justify-center gap-3">
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { clearViewerEndpointFailures(); fetchViewersPage(1, limit); fetchHeaderStats(true); }}>
                    Retry
                  </Button>
                  <Button size="sm" className="bg-transparent text-slate-700" onClick={() => { navigate('/search'); onClose(); }}>
                    Search profiles
                  </Button>
                </div>
              </div>
            ) : loading ? (
              <>
                {layout === 'grid' ? (
                  <div className="grid grid-cols-3 gap-x-4 gap-y-6">
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <div key={i} className={`animate-pulse bg-white border-2 border-rose-100 rounded-lg p-4 h-36 ${compact ? 'text-sm' : ''}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-14 h-14 rounded-full bg-slate-200" />
                          <div className="flex-1">
                            <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                            <div className="h-3 bg-slate-200 rounded w-1/2" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {Array.from({ length: skeletonCount }).map((_, i) => (
                      <div key={i} className="animate-pulse bg-white border border-slate-100 rounded-lg p-3 flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-slate-200" />
                        <div className="flex-1">
                          <div className="h-4 bg-slate-200 rounded w-1/3 mb-2" />
                          <div className="h-3 bg-slate-200 rounded w-1/4" />
                        </div>
                        <div className="w-24 h-8 bg-slate-200 rounded" />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : error ? (
              <div className="py-6 text-center text-sm text-red-600">{error}</div>
            ) : displayedItems.length === 0 ? (
              <div className="py-16 text-center">
                <div className="mx-auto w-28 h-28 rounded-full bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center mb-4">
                  <User className="w-8 h-8 text-purple-600" />
                </div>
                <h4 className="text-lg font-semibold text-slate-800 mb-2">No recent unique viewers</h4>
                <p className="text-sm text-slate-500">No unique profiles have viewed your profile in the selected period.</p>
              </div>
            ) : (
              <>
                {layout === 'grid' ? (
                  // 3 columns x 2 rows grid with larger row gap to match ShortlistModal
                  <div className="grid grid-cols-3 gap-x-4 gap-y-6">
                    {displayedItems.map(v => (
                      <article key={v.userId} className={`bg-white border-2 border-rose-200 rounded-lg p-4 flex flex-col gap-3 ${compact ? 'text-sm' : ''}`} aria-labelledby={`pv-title-${v.userId}`}>
                        <div className="flex items-center gap-3">
                          <Avatar uid={v.userId} size={compact ? 40 : 56} />
                          <div className="flex-1 min-w-0">
                            <div id={`pv-title-${v.userId}`} className="font-medium text-slate-800 truncate">{v.firstName || '—'} {v.lastName || ''}</div>
                            <div className="text-xs text-slate-400 mt-1">ID: <span className="font-mono text-xs text-slate-600">{v.userId}</span></div>
                            <div className="text-xs text-slate-500 mt-2 truncate">{getProfession(v.userId) || v.profession || 'Profession not available'}</div>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="text-sm text-slate-700">Total No.of times Viewed: <span className="font-semibold">{v.hits}</span></div>
                          <div className="text-xs text-slate-500">{v.lastViewedAt ? formatShortDate(v.lastViewedAt) : '—'}</div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center" onClick={() => navigate(`/search-profile/${encodeURIComponent(v.userId)}`)}>
                              <Eye className="w-4 h-4 mr-2" /> View
                            </Button>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {displayedItems.map(v => (
                      <div key={v.userId} className={`bg-white border-2 border-rose-200 rounded-lg p-3 flex items-center gap-3 ${compact ? 'text-sm' : ''}`} aria-labelledby={`pv-title-${v.userId}`}>
                        <Avatar uid={v.userId} size={compact ? 40 : 56} />
                        <div className="flex-1">
                          <div id={`pv-title-${v.userId}`} className="font-medium text-slate-800">{v.firstName || '—'} {v.lastName || ''}</div>
                          <div className="text-xs text-slate-400">ID: <span className="font-mono text-xs text-slate-600">{v.userId}</span></div>
                          <div className="text-xs text-slate-500 mt-1">{getProfession(v.userId) || v.profession || 'Profession not available'}</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-sm text-slate-700">Total No.of times Viewed: <span className="font-semibold">{v.hits}</span></div>
                          <div className="text-xs text-slate-500">{v.lastViewedAt ? formatShortDate(v.lastViewedAt) : '—'}</div>
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white flex items-center" onClick={() => navigate(`/search-profile/${encodeURIComponent(v.userId)}`)}>
                            <Eye className="w-4 h-4 mr-2" /> View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Pagination footer */}
                <div className="mt-6 flex items-center justify-between">
                  <div className="text-sm text-slate-600">Page {page} of {totalPages}</div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" className="bg-transparent text-slate-700" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
                    <Button size="sm" className="bg-transparent text-slate-700" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end px-4 py-3 border-t">
            <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={onClose}>Done</Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
